// src/services/socket.service.ts
import { io, Socket } from 'socket.io-client';
import { authService } from './auth.service';
import { Order } from '../types';

class SocketService {
    private socket: Socket | null = null;
    private orderSocket: Socket | null = null;
    private listeners: Map<string, Function[]> = new Map();

    connect() {
        const token = authService.getAccessToken();
        const baseURL = (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/+$/, '');

        if (!this.socket || !this.socket.connected) {
            console.log('🔗 Connecting to /messages namespace...');
            console.log('   Base URL:', baseURL);
            console.log('   Token configured:', Boolean(token));

            this.socket = io(`${baseURL}/messages`, {
                transports: ['polling', 'websocket'],
                auth: token ? { token } : {},
                reconnection: true,
                reconnectionAttempts: 10,
                reconnectionDelay: 1000,
            });

            this.socket.on('connect', () => {
                console.log('✅ Socket connected to /messages namespace');
                this.emitEvent('connection:status', { status: 'connected' });
            });

            this.socket.on('disconnect', (reason: string) => {
                console.log('❌ Socket disconnected. Reason:', reason);
                this.emitEvent('connection:status', { status: 'disconnected' });
            });

            this.socket.on('connect_error', (error: any) => {
                console.error('❌ Socket connection error:', error?.message);
                this.emitEvent('connection:error', error);
            });

            this.socket.on('chat:message', (message) => {
                console.log('💬 chat:message received:', message);
                this.emitEvent('chat:message', message);
            });

            this.socket.on('chat:sent', (message) => {
                console.log('✅ chat:sent:', message);
                this.emitEvent('chat:sent', message);
            });

            this.socket.on('chat:message:updated', (message) => {
                console.log('🔄 chat:message:updated (order progress):', message);
                this.emitEvent('chat:message:updated', message);
            });

            this.socket.on('chat:message:reactions', (data) => {
                this.emitEvent('chat:message:reactions', data);
            });

            this.socket.on('chat:unread', (data) => {
                this.emitEvent('chat:unread', data);
            });

            this.socket.on('chat:order:status', (data) => {
                this.emitEvent('chat:order:status', data);
            });

            this.socket.on('chat:history', (data) => {
                console.log('📋 chat:history:', data);
                this.emitEvent('chat:history', data);
            });

            this.socket.on('chat:joined', (data) => {
                console.log('🔗 chat:joined:', data);
                this.emitEvent('chat:joined', data);
            });

            this.socket.on('chat:error', (data) => {
                console.log('❌ chat:error:', data);
                this.emitEvent('chat:error', data);
            });

            this.socket.on('conversations:list', (conversations) => {
                console.log('📑 conversations:list:', conversations);
                this.emitEvent('conversations:list', conversations);
            });

            this.socket.on('user:online', (data) => {
                console.log('🟢 user:online:', data);
                this.emitEvent('user:online', data);
            });

            this.socket.on('user:offline', (data) => {
                console.log('🔴 user:offline:', data);
                this.emitEvent('user:offline', data);
            });

            this.socket.on('visitors:count', (data: { count: number }) => {
                this.emitEvent('visitors:count', data);
            });
        }

        if (!this.orderSocket || !this.orderSocket.connected) {
            console.log('🔗 Connecting to /orders namespace...');
            this.orderSocket = io(`${baseURL}/orders`, {
                transports: ['polling', 'websocket'],
                auth: token ? { token } : {},
                reconnection: true,
                reconnectionAttempts: 10,
                reconnectionDelay: 1000,
            });

            this.orderSocket.on('connect', () => {
                console.log('✅ Socket connected to /orders namespace');
            });

            this.orderSocket.on('disconnect', (reason: string) => {
                console.log('❌ Orders socket disconnected. Reason:', reason);
            });

            this.orderSocket.on('connect_error', (error: any) => {
                console.error('❌ Orders socket connection error:', error?.message);
                this.emitEvent('connection:error', error);
            });

            this.orderSocket.on('order:created', (order) => {
                console.log('📦 order:created:', order);
                this.emitEvent('order:created', order);
            });

            this.orderSocket.on('order:updated', (order) => {
                console.log('🔄 order:updated:', order);
                this.emitEvent('order:updated', order);
            });

            this.orderSocket.on('order:status', (data) => {
                console.log('📊 order:status:', data);
                this.emitEvent('order:status', data);
            });

            this.orderSocket.on('order:step:done', (data) => {
                console.log('✅ order:step:done:', data);
                this.emitEvent('order:step:done', data);
            });

            this.orderSocket.on('orders:list', (orders) => {
                console.log('📦 orders:list:', orders);
                this.emitEvent('orders:list', orders);
            });

            this.orderSocket.on('order:error', (error) => {
                console.log('❌ order:error:', error);
                this.emitEvent('order:error', error);
            });

            this.orderSocket.on('order:confirmation-required', (data) => {
                console.log('⚠️ order:confirmation-required:', data);
                this.emitEvent('order:confirmation-required', data);
            });
        }
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        if (this.orderSocket) {
            this.orderSocket.disconnect();
            this.orderSocket = null;
        }
    }

    emit(event: string, data?: any) {
        const targetSocket = event.startsWith('order:') || event.startsWith('orders:') ? this.orderSocket : this.socket;

        if (!targetSocket) {
            console.warn('⚠️ Socket not initialized for event:', event);
            return;
        }
        if (!targetSocket.connected) {
            console.warn('⚠️ Socket not connected for event:', event);
            return;
        }
        console.log('📤 Emitting event:', event, data);
        targetSocket.emit(event, data);
    }

    on(event: string, callback: Function) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event)?.push(callback);
    }

    off(event: string, callback?: Function) {
        if (!callback) {
            this.listeners.delete(event);
            return;
        }
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    private emitEvent(event: string, data: any) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(cb => cb(data));
        }
    }

    isConnected(): boolean {
        return this.socket?.connected || false;
    }

    // Chat methods
    sendMessage(content: string, roomId: string) {
        this.sendReplyMessage(content, roomId);
    }

    sendReplyMessage(content: string, roomId: string, replyTo?: string) {
        console.log('💬 Sending message:', { content, roomId, replyTo });
        this.emit('chat:send', { content, roomId, replyTo });
    }

    toggleMessageReaction(messageId: string, emoji: string): Promise<{ success: boolean; error?: string }> {
        if (!this.socket?.connected) {
            return Promise.reject(new Error('Chưa kết nối máy chủ tin nhắn.'));
        }

        return new Promise((resolve, reject) => {
            this.socket!.timeout(5000).emit(
                'chat:reaction:toggle',
                { messageId, emoji },
                (timeoutError: Error | null, response: { success: boolean; error?: string }) => {
                    if (timeoutError) {
                        reject(new Error('Máy chủ chưa phản hồi khi cập nhật cảm xúc.'));
                        return;
                    }
                    if (!response?.success) {
                        reject(new Error(response?.error || 'Không thể cập nhật cảm xúc.'));
                        return;
                    }
                    resolve(response);
                },
            );
        });
    }

    markMessageUnread(messageId: string): Promise<{ success: boolean; roomId?: string; error?: string }> {
        if (!this.socket?.connected) {
            return Promise.reject(new Error('Chưa kết nối máy chủ tin nhắn.'));
        }

        return new Promise((resolve, reject) => {
            this.socket!.timeout(5000).emit(
                'chat:mark-unread',
                { messageId },
                (timeoutError: Error | null, response: { success: boolean; roomId?: string; error?: string }) => {
                    if (timeoutError) {
                        reject(new Error('Máy chủ chưa phản hồi khi đánh dấu tin nhắn.'));
                        return;
                    }
                    if (!response?.success) {
                        reject(new Error(response?.error || 'Không thể đánh dấu tin nhắn chưa đọc.'));
                        return;
                    }
                    resolve(response);
                },
            );
        });
    }

    joinRoom(roomId: string) {
        console.log('🔗 Joining room:', roomId);
        this.emit('chat:join', { roomId });
    }

    leaveRoom(roomId: string) {
        console.log('👋 Leaving room:', roomId);
        this.emit('chat:leave', { roomId });
    }

    getHistory(roomId: string, limit: number = 10, before?: string) {
        console.log('📜 Getting history:', { roomId, limit, before });
        this.emit('chat:history', { roomId, limit, before });
    }

    getConversations(userId: string, isAdmin: boolean = false) {
        console.log('📑 Requesting conversations:', { userId, isAdmin });
        this.emit('conversations:list', { userId, isAdmin });
    }

    // Order methods
    createOrder(code: string, totalSteps: number = 1, note?: string) {
        console.log('📦 Creating order:', { code, totalSteps, hasNote: Boolean(note) });
        this.emit('order:create', { code, totalSteps, note });
    }

    createOrders(
        orders: Array<{ code: string; totalSteps: number }>,
        confirmed = false,
        note?: string,
    ): Promise<{ success: boolean; orders?: unknown[]; error?: string }> {
        console.log('📦 Creating orders in bulk:', { orders, hasNote: Boolean(note) });
        if (!this.orderSocket?.connected) {
            return Promise.reject(new Error('Chưa kết nối máy chủ đơn hàng. Vui lòng thử lại.'));
        }

        return new Promise((resolve, reject) => {
            this.orderSocket!.timeout(15000).emit(
                'orders:create:bulk',
                { orders, confirmed, note },
                (timeoutError: Error | null, response: { success: boolean; orders?: unknown[]; error?: string }) => {
                if (timeoutError) {
                    reject(new Error('Máy chủ chưa phản hồi. Vui lòng thử lại.'));
                    return;
                }
                if (!response?.success) {
                    reject(new Error(response?.error || 'Không tạo được đơn hàng.'));
                    return;
                }
                resolve(response);
                },
            );
        });
    }

    recreateOrder(orderId: string, totalSteps: number) {
        console.log('📦 Recreating order:', { orderId, totalSteps });
        this.emit('order:recreate', { orderId, totalSteps });
    }

    requestCompletedOrderRedo(orderId: string, reason: string): Promise<{ success: boolean; order?: Order; error?: string }> {
        return this.emitOrderRequest('order:redo:request', { orderId, reason });
    }

    approveCompletedOrderRedo(orderId: string): Promise<{ success: boolean; order?: Order; error?: string }> {
        return this.emitOrderRequest('order:redo:approve', { orderId });
    }

    requestCompletedOrderRefund(orderId: string, reason: string): Promise<{ success: boolean; order?: Order; error?: string }> {
        return this.emitOrderRequest('order:refund:request', { orderId, reason });
    }

    approveCompletedOrderRefund(orderId: string): Promise<{ success: boolean; order?: Order; error?: string }> {
        return this.emitOrderRequest('order:refund:approve', { orderId });
    }

    private emitOrderRequest(
        event: string,
        data: Record<string, string>,
    ): Promise<{ success: boolean; order?: Order; error?: string }> {
        if (!this.orderSocket?.connected) {
            return Promise.reject(new Error('Chưa kết nối máy chủ đơn hàng.'));
        }
        return new Promise((resolve, reject) => {
            this.orderSocket!.timeout(10000).emit(
                event,
                data,
                (timeoutError: Error | null, response: { success: boolean; order?: Order; error?: string }) => {
                    if (timeoutError) {
                        reject(new Error('Máy chủ chưa phản hồi. Vui lòng thử lại.'));
                        return;
                    }
                    if (!response?.success) {
                        reject(new Error(response?.error || 'Không thể xử lý yêu cầu đơn hàng.'));
                        return;
                    }
                    resolve(response);
                },
            );
        });
    }

    processOrder(orderId: string) {
        console.log('🔄 Processing order:', orderId);
        this.emit('order:process', { orderId });
    }

    stepOrder(orderId: string) {
        console.log('➡️ Stepping order:', orderId);
        this.emit('order:step', { orderId });
    }

    stepsOrder(orderId: string, steps: number) {
        console.log('⏭️ Stepping order multiple steps:', { orderId, steps });
        this.emit('order:steps', { orderId, steps });
    }

    resetOrder(orderId: string) {
        console.log('🔁 Resetting order:', orderId);
        this.emit('order:reset', { orderId });
    }

    cancelOrder(orderId: string) {
        console.log('❌ Cancelling order:', orderId);
        this.emit('order:cancel', { orderId });
    }

    claimOrderCode(orderId: string) {
        console.log('📌 Claiming order code:', orderId);
        this.emit('order:claim-code', { orderId });
    }

    getOrderStatus(orderId: string) {
        console.log('📊 Getting order status:', orderId);
        this.emit('order:status', { orderId });
    }

    getOrders() {
        console.log('📋 Requesting orders list');
        this.emit('orders:list', {});
    }
}

export const socketService = new SocketService();